// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaEntry,
  $FullRegattaGroup,
  $Model,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';
import {
  GroupType,
  ratingKeyValue,
  RegattaGroupRatingType,
  RegattaRegistrationStatus,
  RegattaStatus,
} from 'common.data';

// Global Components
import * as React from 'react';
import ModalImage from 'react-modal-image';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getDateTimeFormats,
  getRegattaEntryCrewmemberNames,
  getRegattaEntryRatingValue,
  getRegattaEntrySkipperName,
} from 'common.helpers/dist/browser';
import {
  getRegattaGroupName,
} from '../../../../helpers';
import config from '../../../../config';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import RegattaEntryDialog from '../../../dialog/RegattaEntryDialog';
import LabelQuantity from '../../../common/LabelQuantity';
import RegistrationNo from '../../../common/RegistrationNo';
import NoData from '../../../common/NoData';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  editRegattaEntry: (arg0: {
    RegattaEntryId: string;
  }) => void;
  item: $Model<'Regatta'>;
};

export type $StateProps = {
  auth: $ReducerState<'auth'>;
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$FullRegattaEntry>;
  loading: boolean;
};

class EntriesSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getRegattaEntriesSectionData({
        RegattaId: item.id,
      });

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.RegattaId === item.id && data.modelName === 'RegattaEntry') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  editItem(dataItem: $FullRegattaEntry): void {
    const {
      editRegattaEntry,
    } = this.props;

    editRegattaEntry({
      RegattaEntryId: dataItem.id,
    });
  }

  renderDataItemButtons({
    dataItem,
  }: {
    dataItem: $FullRegattaEntry;
  }) {
    const {
      auth,
      item,
    } = this.props;

    const isSkipper: boolean = _.get(
      auth,
      'user.Email',
    ) === _.get(
      dataItem,
      'RegattaEntryCrewmembers[0].Email',
    );

    const isRegistrationOngoing: boolean = item.RegistrationStatus === RegattaRegistrationStatus
      .ongoing;
    const showRegistrationEditButton: boolean = isSkipper && isRegistrationOngoing;

    return (
      <div className={'flex flex-row justify-end'}>
        {showRegistrationEditButton === true && (
          <button
            className={'btn btn_success btn_outlined uppercase px-2 py-1 mr-2'}
            onClick={() => this.editItem(dataItem)}
            type={'button'}
          >
            <em className={'la la-edit text-2xl'} />
          </button>
        )}
        <RegattaEntryDialog
          item={dataItem}
          regatta={item}
        >
          <button
            className={'btn btn_primary btn_outlined uppercase px-2 py-1'}
            type={'button'}
          >
            <em className={'la la-sms text-2xl'} />
          </button>
        </RegattaEntryDialog>
      </div>
    );
  }

  renderSportItem({
    dataItem,
    dateTimeFormat,
  }: {
    dataItem: $FullRegattaEntry;
    dateTimeFormat: string;
  }) {
    const {
      item,
    } = this.props;

    const createdAtMoment = moment(dataItem.createdAt);

    return (
      <tr key={dataItem.id}>
        <td className={'leading-none'}>
          <RegistrationNo
            item={dataItem}
            regatta={item}
          />
        </td>
        <td className={'leading-none'}>
          <div className={'text-base'}>
            {getRegattaEntrySkipperName({
              regattaEntry: dataItem,
            })}
          </div>
          {dataItem.RegattaEntryCrewmembers.length > 1 && (
            <small className={'text-gray-600'}>
              {getRegattaEntryCrewmemberNames({
                excludeSkipper: true,
                regattaEntry: dataItem,
              }).join(', ')}
            </small>
          )}
        </td>
        <td>
          {dataItem.Harbor}
        </td>
        <td>
          {dataItem.Club}
        </td>
        <td>
          {dataItem.CoachName}
        </td>
        <td
          className={'leading-tight text-center'}
          title={String(dataItem.createdAt)}
        >
          <div>
            {createdAtMoment.fromNow()}
          </div>
          <small>
            {createdAtMoment.format(dateTimeFormat)}
          </small>
        </td>
        <td>
          {this.renderDataItemButtons({
            dataItem,
          })}
        </td>
      </tr>
    );
  }

  renderCruiserItem({
    dataItem,
    dateTimeFormat,
    ratingType,
  }: {
    dataItem: $FullRegattaEntry;
    dateTimeFormat: string;
    ratingType: RegattaGroupRatingType;
    regattaGroup?: $FullRegattaGroup;
  }) {
    const {
      item,
      t,
    } = this.props;

    const createdAtMoment = moment(dataItem.createdAt);

    return (
      <tr key={dataItem.id}>
        <td className={'py-1-i'}>
          {dataItem.Image ? (
            <ModalImage
              large={`${config.CDN_URL}/images/max/${dataItem.Image}`}
              small={`${config.CDN_URL}/images/sqr/${dataItem.Image}`}
            />
          ) : (
            <img
              alt={t('Logo')}
              className={'max-h-16'}
              src={`${config.CDN_URL}/static/dummy.png`}
            />
          )}
        </td>
        <td className={'leading-none'}>
          <RegistrationNo
            item={dataItem}
            regatta={item}
          />
        </td>
        <td>
          <div className={'text-base'}>
            {dataItem.Model}
          </div>
        </td>
        <td className={'leading-none'}>
          <div
            className={'text-base'}
            title={t('Boat name')}
          >
            {dataItem.Name}
          </div>
          <small className={'text-gray-600'}>
            {_.get(
              dataItem,
              'RegattaEntryCrewmembers[0].Name',
            )}
          </small>
        </td>
        <td
          className={'leading-tight text-center'}
          title={String(dataItem.createdAt)}
        >
          <div>
            {createdAtMoment.fromNow()}
          </div>
          <small>
            {createdAtMoment.format(dateTimeFormat)}
          </small>
        </td>
        <td className={'text-center'}>
          {ratingType !== RegattaGroupRatingType.monotype && (
            <span>
              {getRegattaEntryRatingValue({
                regattaEntry: dataItem,
                type: ratingType,
              })}
            </span>
          )}
        </td>
        <td className={'text-right'}>
          {this.renderDataItemButtons({
            dataItem,
          })}
        </td>
      </tr>
    );
  }

  renderSportGroup({
    data,
    dateTimeFormat,
    regattaGroup,
  }: {
    data: Array<$FullRegattaEntry>;
    dateTimeFormat: string;
    regattaGroup?: $FullRegattaGroup;
  }) {
    const {
      t,
    } = this.props;

    const regattaGroupName: string = getRegattaGroupName(regattaGroup) || t('Ungrouped');

    const groupCrewmemberQuantity: number = _.get(
      regattaGroup,
      'Group.CrewQuantity',
      1,
    );

    return (
      <table className={'table table_hoverable w-full'}>
        <thead>
          <tr>
            <th className={'text-left uppercase text-green-600 min-w-20'}>
              {regattaGroupName}
            </th>
            <th className={'text-left uppercase'}>
              {groupCrewmemberQuantity > 1 ? `${t('Skipper')} / ${t('Crew')}` : t('Skipper')}
            </th>
            <th className={'text-left uppercase'}>
              {t('City')}
            </th>
            <th className={'text-left uppercase'}>
              {t('Club')}
            </th>
            <th className={'text-left uppercase'}>
              {t('Coach')}
            </th>
            <th className={'text-center uppercase'}>
              {t('Registration date')}
            </th>
            <th className={'text-right uppercase'}>
              <LabelQuantity value={data.length} />
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $FullRegattaEntry) => this.renderSportItem({
            dataItem,
            dateTimeFormat,
          }))}
        </tbody>
      </table>
    );
  }

  renderCruiserGroup({
    data,
    dateTimeFormat,
    regattaGroup,
  }: {
    data: Array<$FullRegattaEntry>;
    dateTimeFormat: string;
    regattaGroup?: $FullRegattaGroup;
  }) {
    const {
      item,
      t,
    } = this.props;

    const ratingType: RegattaGroupRatingType = _.get(
      regattaGroup,
      'RatingType',
      RegattaGroupRatingType.monotype,
    );

    const ratingKeyValueName: string = ratingKeyValue[ratingType];

    const regattaGroupName: string = getRegattaGroupName(regattaGroup) || t('Ungrouped');

    return (
      <table className={'table table_hoverable w-full'}>
        <thead>
          <tr>
            <th className={'text-left uppercase text-green-600 min-w-20'}>
              {regattaGroupName}
            </th>
            <th className={'text-left uppercase'}>
              {item.CountryEnabled === true ? t('Country') : t('Reg. no.')}
            </th>
            <th className={'text-left uppercase'}>
              {t('Make / Model')}
            </th>
            <th className={'text-left uppercase'}>
              {[t('Title'), t('Skipper')].join(' / ')}
            </th>
            <th className={'text-center uppercase'}>
              {t('Registration date')}
            </th>
            <th className={'text-center uppercase'}>
              {ratingType !== RegattaGroupRatingType.monotype && (
                <span title={t('Rating')}>
                  <span className={'uppercase'}>
                    {`${ratingType}: `}
                  </span>
                  <span className={'text-gray-600'}>
                    {ratingKeyValueName}
                  </span>
                </span>
              )}
            </th>
            <th className={'text-right uppercase'}>
              <LabelQuantity value={data.length} />
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $FullRegattaEntry) => this.renderCruiserItem({
            dataItem,
            dateTimeFormat,
            ratingType,
            regattaGroup,
          }))}
        </tbody>
      </table>
    );
  }

  renderGroupData({
    data,
    regattaGroup,
  }: {
    data: Array<$FullRegattaEntry>;
    regattaGroup?: $FullRegattaGroup;
  }) {
    const {
      main,
    } = this.props;

    const formats = getDateTimeFormats({
      RegionId: main.RegionId,
      regions: main.regions,
    });

    const dateTimeFormat = `${formats.Date} ${formats.Time}`;

    const groupView = {
      [GroupType.cruiser]: () => this.renderCruiserGroup({
        data,
        dateTimeFormat,
        regattaGroup,
      }),
      [GroupType.sport]: () => this.renderSportGroup({
        data,
        dateTimeFormat,
        regattaGroup,
      }),
    };

    const regattaGroupType: GroupType = _.get(
      regattaGroup,
      'Group.Type',
      GroupType.cruiser,
    );

    return (
      <div className={'overflow-x-auto'}>
        {groupView[regattaGroupType]()}
      </div>
    );
  }

  render() {
    const {
      item,
      t,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    const groupedData: Record<string, Array<$FullRegattaEntry>> = {
    };

    const regattaGroups: Array<$FullRegattaGroup> = [];

    data.forEach((dataItem: $FullRegattaEntry) => {
      const regattaGroupId: string = dataItem.RegattaGroupId || 'undefined';

      if (!groupedData[regattaGroupId]) {
        groupedData[regattaGroupId] = [];

        if (dataItem.RegattaGroup) {
          regattaGroups.push(dataItem.RegattaGroup);
        }
      }

      groupedData[regattaGroupId].push(dataItem);
    });

    const hasData: boolean = data.length > 0;

    const shorten: boolean = _.includes(
      [
        RegattaStatus.upcoming,
        RegattaStatus.new,
      ],
      item.Status,
    ) === false && data.length > 5;

    return (
      <Section
        id={webSections[webPages.regattaPage].entries}
        shorten={shorten}
        title={t('Entries')}
        transparent={hasData}
      >
        <Loader
          fade
          loading={loading === true}
        >
          {hasData === true ? (
            <div className={shorten === true ? '-mb-3' : '-mx-5 -mb-8'}>
              {_.map(
                groupedData,
                (regattaEntries: Array<$FullRegattaEntry>, regattaGroupId: string) => {
                  const regattaGroup: $FullRegattaGroup | undefined = _.find(
                    regattaGroups,
                    {
                      id: regattaGroupId,
                    },
                  );

                  return (
                    <div
                      className={'bg-white rounded-lg mb-3 px-5 pb-2'}
                      key={regattaGroupId}
                    >
                      {this.renderGroupData({
                        data: regattaEntries,
                        regattaGroup,
                      })}
                    </div>
                  );
                },
              )}
            </div>
          ) : (
            <NoData label={t('No regatta entries yet')} />
          )}
        </Loader>
      </Section>
    );
  }
}

export default EntriesSectionView;
